'use client';

import ThemedIcon from '@/components/ui/ThemedIcon';
import { PathEnum } from '@/i18n/locale-config';
import { usePathname, useRouter } from '@/i18n/navigation';
import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ScheduleSortOption } from './ScheduleSorting';

interface MobileScheduleSortingProps {
    currentSort: ScheduleSortOption;
}

const MobileScheduleSorting = ({ currentSort }: MobileScheduleSortingProps) => {
    const t = useTranslations();
    const router = useRouter();
    const pathname = usePathname();
    const [isOpen, setIsOpen] = useState(false);

    const sortOptions: { value: ScheduleSortOption; label: string }[] = [
        { value: 'date', label: t('Sorting.byDate') },
        { value: 'alphabetical', label: t('Sorting.alphabetical') },
    ];

    const resolveBasePath = () => {
        const pathKey = pathname as keyof typeof PathEnum;
        return PathEnum[pathKey] ?? pathname;
    };

    const handleSortChange = (sortValue: ScheduleSortOption) => {
        const params = new URLSearchParams(window.location.search);
        params.set('sort', sortValue);
        const basePath = resolveBasePath();
        const query = params.toString();
        router.push(query ? `${basePath}?${query}` : basePath);
        setIsOpen(false);
    };

    const currentSortLabel =
        sortOptions.find((option) => option.value === currentSort)?.label || t('Sorting.byDate');

    return (
        <div className="relative flex-1">
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="flex items-center justify-between w-full px-3 py-2 text-left bg-transparent border border-white text-white"
            >
                <span className="text-sm font-helvetica-neue font-light">{currentSortLabel}</span>
                <ThemedIcon
                    aria-label="Toggle sorting options"
                    className={`w-4 h-4 transition-transform duration-300 text-primary ${isOpen ? 'rotate-180' : ''}`}
                    src="/img/icons/arrow.svg"
                    width={16}
                    height={16}
                />
            </button>

            {isOpen && (
                <div className="absolute top-full left-0 w-full bg-black border border-white border-t-0 z-10">
                    {sortOptions.map((option) => (
                        <button
                            key={option.value}
                            onClick={() => handleSortChange(option.value)}
                            className={`w-full px-3 py-2 text-left text-sm font-helvetica-neue font-light hover:bg-primary/10 transition-colors duration-300 ${
                                currentSort === option.value ? 'text-primary' : 'text-white'
                            }`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default MobileScheduleSorting;
